import { useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { AlertTriangle, CheckCircle2, GitBranch, Laptop, Loader2, XCircle } from "lucide-react";
import type { Team } from "@/lib/api/types";
import { useSetProjectRepository, useTeamMembers } from "@/hooks/queries/use-teams";
import { useEnqueueJob, useJob, useMachines } from "@/hooks/queries/use-mailbox";
import { useOnboardingState } from "@/hooks/queries/use-onboarding";
import { machineReadiness, machineServicesRemote, type Machine } from "@/lib/api/mailbox";
import { useAuthStore } from "@/stores/auth-store";
import { useGitHubStatus, useProjectRepository } from "@/hooks/queries/use-github";
import { useCloudStatus } from "@/hooks/queries/use-cloud";
import { projectCloudApi, type RepoVariablesSync } from "@/lib/api/cloud";
import { ConnectGitHubGate } from "@/components/github/connect-github-gate";
import { projectGitHubReturnTo } from "@/lib/github-return-to";
import { GitHubRepoPicker } from "./github-repo-picker";
import { ProjectStackCard } from "./project-stack-card";
import { ProjectDatabaseCard } from "./project-database-card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

function errorText(error: unknown): string | null {
  if (!error) return null;
  return error instanceof Error ? error.message : "Something went wrong. Try again.";
}

function repoName(remote: string): string {
  return remote.replace(/\/+$/, "").split("/").pop() ?? "project";
}

/** The outcome of a "set up on my computer" job, shown inline while it runs. */
function SetupProgress({ jobId }: { jobId: string }) {
  const { data: job } = useJob(jobId);
  if (!job || job.status === "pending" || job.status === "claimed" || job.status === "running") {
    return (
      <p className="flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" />
        {job?.status === "pending" ? "Waiting for the computer to pick this up…" : "Setting up…"}
      </p>
    );
  }
  if (job.status === "cancelling" || job.status === "waiting") {
    return (
      <p className="flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" />
        {job.status === "waiting" ? "Waiting to resume…" : "Cancelling…"}
      </p>
    );
  }
  if (job.status === "cancelled") {
    return (
      <p className="flex items-center gap-2 text-xs text-muted-foreground">
        <XCircle className="h-3 w-3" />
        Setup was cancelled.
      </p>
    );
  }
  const result = (job.result ?? {}) as { message?: unknown; error?: unknown };
  if (job.status === "done") {
    return (
      <p className="flex items-center gap-2 text-xs text-emerald-300">
        <CheckCircle2 className="h-3 w-3" />
        {typeof result.message === "string" ? result.message : "Set up."}
      </p>
    );
  }
  return (
    <p className="flex items-start gap-2 text-xs text-destructive">
      <XCircle className="mt-0.5 h-3 w-3 shrink-0" />
      {typeof result.error === "string" ? result.error : "Setting up failed."}
    </p>
  );
}

function MachineRow({ machine, team, remote }: { machine: Machine; team: Team; remote: string }) {
  const enqueue = useEnqueueJob();
  const [folder, setFolder] = useState("");
  const [open, setOpen] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const ready = machineServicesRemote(machine, remote);
  const online = machineReadiness(machine) !== "offline";

  async function start() {
    const job = await enqueue.mutateAsync({
      machineId: machine.id,
      type: "connect_repo",
      payload: { teamId: team.id, ...(folder.trim() ? { folder: folder.trim() } : {}) },
    });
    setJobId(job.id);
    setOpen(false);
  }

  return (
    <li className="space-y-2 rounded-md border border-white/[0.06] p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex items-center gap-2 text-sm">
          <Laptop className="h-4 w-4" />
          {machine.name}
        </span>
        {ready ? (
          <span className="flex items-center gap-1 text-xs text-emerald-300">
            <CheckCircle2 className="h-3 w-3" />
            Set up
          </span>
        ) : (
          !open &&
          !jobId && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setOpen(true);
              }}
            >
              Set up on this computer
            </Button>
          )
        )}
      </div>
      {open ? (
        <div className="space-y-2">
          <Label htmlFor={`repo-folder-${machine.id}`} className="text-xs">
            Folder on {machine.name} (optional)
          </Label>
          <Input
            id={`repo-folder-${machine.id}`}
            placeholder={`~/CompBox/${repoName(remote)}`}
            value={folder}
            onChange={(e) => {
              setFolder(e.target.value);
            }}
          />
          <p className="text-xs text-muted-foreground">
            If that folder already has a copy of the repository, it's used as is. Otherwise the
            computer downloads it with its own Git sign-in.
            {!online ? ` ${machine.name} is offline right now; this will run when it's back.` : ""}
          </p>
          {errorText(enqueue.error) ? (
            <p className="text-xs text-destructive">{errorText(enqueue.error)}</p>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={enqueue.isPending}
              onClick={() => void start().catch(() => undefined)}
            >
              Set up
            </Button>
          </div>
        </div>
      ) : null}
      {jobId && !ready ? <SetupProgress jobId={jobId} /> : null}
    </li>
  );
}

/** Steps to give the CompBox GitHub App permission to write repository variables. */
const VARIABLES_PERMISSION_STEPS = [
  "On GitHub, open Settings → Developer settings → GitHub Apps → MyCompBox.",
  "Open Permissions → Repository permissions and set Variables to “Read and write”, then Save.",
  "Approve the permission update on the app's installation.",
  "Come back here and click Sync to repo again.",
];

/**
 * "Sync to repo": writes the project's Google Cloud facts (GCP_WORKLOAD_IDENTITY_PROVIDER,
 * GCP_PROJECT_ID, GCP_REGION, GCP_SERVICE_ACCOUNT) to its GitHub repository's variables.
 */
export function RepoVariablesSyncRow({ teamId }: { teamId: string }) {
  const sync = useMutation({
    mutationFn: async () => (await projectCloudApi.syncRepo(teamId)).data,
  });
  const result: RepoVariablesSync | undefined = sync.data;
  const written = (result?.variables ?? [])
    .filter((v) => v.status === "created" || v.status === "updated")
    .map((v) => v.name);
  const failed = (result?.variables ?? []).filter((v) => v.status === "error");

  return (
    <div className="space-y-2 rounded-lg border border-white/[0.08] p-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-muted-foreground">
          GitHub variables: the deploy workflows read your Google Cloud settings from the
          repository's Actions variables.
        </p>
        <Button
          size="sm"
          variant="outline"
          className="shrink-0"
          disabled={sync.isPending}
          onClick={() => {
            sync.mutate();
          }}
        >
          {sync.isPending ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
          Sync to repo
        </Button>
      </div>
      {result?.status === "synced" ? (
        <p className="flex items-start gap-2 text-xs text-emerald-300">
          <CheckCircle2 className="mt-0.5 h-3 w-3 shrink-0" />
          Synced: {written.join(", ")}
        </p>
      ) : null}
      {result?.status === "skipped" ? (
        <p className="text-xs text-muted-foreground">{result.reason ?? "Nothing to sync."}</p>
      ) : null}
      {result?.status === "needs-permission" ? (
        <div className="space-y-1 text-xs text-amber-300">
          <p className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
            The CompBox GitHub app can't write repository variables yet.
            {written.length > 0 ? ` Synced: ${written.join(", ")}.` : ""}
          </p>
          <ol className="list-decimal space-y-0.5 pl-6 text-muted-foreground">
            {VARIABLES_PERMISSION_STEPS.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        </div>
      ) : null}
      {result?.status === "error" ? (
        <p className="flex items-start gap-2 text-xs text-destructive">
          <XCircle className="mt-0.5 h-3 w-3 shrink-0" />
          Couldn't sync {failed.map((v) => v.name).join(", ")}
          {failed[0]?.message ? `: ${failed[0].message}` : "."}
        </p>
      ) : null}
      {errorText(sync.error) ? <p className="text-xs text-destructive">{errorText(sync.error)}</p> : null}
    </div>
  );
}

/**
 * The project's repository: pick it from GitHub (project or organization
 * admins; pasting a clone URL is the fallback for other Git hosts), and set it
 * up on your paired computers so runs for this project land in the right folder.
 */
export function ProjectRepositoryCard({ team }: { team: Team }) {
  const me = useAuthStore((s) => s.user);
  const { data: onboarding } = useOnboardingState();
  const { data: members } = useTeamMembers(team.id);
  const { data: machines } = useMachines();
  const setRepo = useSetProjectRepository(team.id);
  const returnTo = projectGitHubReturnTo(team.id);
  const { data: github } = useGitHubStatus(returnTo);
  const { data: projectRepo } = useProjectRepository(team.id);
  const { data: cloud } = useCloudStatus();
  const defaultCloud = cloud?.connections.find((c) => c.isDefault) ?? null;
  // "github": pick or create through the CompBox GitHub App; "paste": any Git URL.
  const [editing, setEditing] = useState<false | "github" | "paste">(false);
  const [url, setUrl] = useState("");
  const githubReady = !!github?.configured && github.installations.some((i) => !i.suspended);
  const [params] = useSearchParams();
  const wantsConnect = params.get("connect") === "github";
  const rootRef = useRef<HTMLDivElement>(null);
  const openedFromUrl = useRef(false);

  const remote = team.gitRemote ?? null;
  const myRole = members?.find((m) => m.user.id === me?.id)?.role;
  const canEdit =
    !!me?.isGlobalAdmin ||
    (onboarding?.step === "ready" && onboarding.organizationRole === "admin") ||
    myRole === "admin";
  const paired = (machines ?? []).filter((m) => m.status !== "revoked");

  // ?connect=github (e.g. back from installing the GitHub app): scroll here and open the picker.
  useEffect(() => {
    const el = rootRef.current;
    // jsdom (tests) has no scrollIntoView.
    if (wantsConnect && el && "scrollIntoView" in el)
      el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [wantsConnect]);
  useEffect(() => {
    if (!wantsConnect || openedFromUrl.current || !canEdit || !githubReady) return;
    openedFromUrl.current = true;
    setEditing("github");
  }, [wantsConnect, canEdit, githubReady]);

  async function save(value: string | null) {
    await setRepo.mutateAsync(value);
    setEditing(false);
    setUrl("");
  }

  return (
    <div ref={rootRef} className="space-y-4">
      <div>
        <h3 className="text-lg font-medium">Repository</h3>
        <p className="text-sm text-muted-foreground">
          The Git repository this project's code lives in. Work sent to a computer runs in that
          computer's copy of it.
        </p>
      </div>

      {editing === "github" && github ? (
        <GitHubRepoPicker
          teamId={team.id}
          projectName={team.name}
          installations={github.installations}
          onDone={() => {
            setEditing(false);
          }}
          onPasteInstead={() => {
            setEditing("paste");
            setUrl(remote ?? "");
          }}
        />
      ) : editing ? (
        <div className="space-y-2 rounded-lg border border-white/[0.08] p-4">
          <Label htmlFor="project-repo-url">Repository URL</Label>
          <Input
            id="project-repo-url"
            placeholder="https://github.com/your-company/your-app"
            value={url}
            onChange={(e) => {
              setUrl(e.target.value);
            }}
          />
          <p className="text-xs text-muted-foreground">
            Paste it the way you'd clone it: https://… or git@github.com:… Never include a password
            or token.
          </p>
          <p className="text-xs text-muted-foreground">
            Stack and Cloud deploys need a GitHub repository.
          </p>
          {errorText(setRepo.error) ? (
            <p className="text-sm text-destructive">{errorText(setRepo.error)}</p>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setEditing(false);
              }}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={!url.trim() || setRepo.isPending}
              onClick={() => void save(url.trim()).catch(() => undefined)}
            >
              Save
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-2 rounded-lg border border-white/[0.08] p-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-center gap-2 text-sm">
            <GitBranch className="h-4 w-4 shrink-0" />
            {remote ? (
              <>
                <a
                  href={remote}
                  target="_blank"
                  rel="noreferrer"
                  className="truncate text-primary hover:underline"
                >
                  {remote}
                </a>
                {projectRepo?.defaultBranch ? (
                  <span className="shrink-0 rounded bg-white/[0.06] px-1.5 py-0.5 text-xs text-muted-foreground">
                    {projectRepo.defaultBranch}
                  </span>
                ) : null}
                {projectRepo?.github && !projectRepo.needsAttention ? (
                  <span className="flex shrink-0 items-center gap-1 text-xs text-emerald-300">
                    <CheckCircle2 className="h-3 w-3" />
                    {projectRepo.linkSource === "auto"
                      ? "Linked through GitHub automatically"
                      : "connected through GitHub"}
                  </span>
                ) : null}
              </>
            ) : (
              <span className="text-muted-foreground">No repository connected yet.</span>
            )}
          </div>
          {canEdit ? (
            <div className="flex shrink-0 gap-2">
              {remote || githubReady ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setEditing(githubReady ? "github" : "paste");
                    setUrl(remote ?? "");
                  }}
                >
                  {remote ? "Change" : "Connect a repository"}
                </Button>
              ) : null}
              {remote ? (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={setRepo.isPending}
                  onClick={() => void save(null).catch(() => undefined)}
                >
                  Disconnect
                </Button>
              ) : null}
            </div>
          ) : !remote ? (
            <span className="text-xs text-muted-foreground">
              Ask a project admin to connect it.
            </span>
          ) : null}
        </div>
      )}

      {!editing && projectRepo?.needsAttention ? (
        <div className="flex flex-col gap-2 rounded-lg border border-amber-400/30 bg-amber-400/5 p-3 text-sm sm:flex-row sm:items-center sm:justify-between">
          <span className="flex items-start gap-2 text-amber-300">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            This repository was removed from the CompBox app on GitHub.
          </span>
          {canEdit && githubReady ? (
            <Button
              size="sm"
              variant="outline"
              className="shrink-0"
              onClick={() => {
                setEditing("github");
              }}
            >
              Pick again
            </Button>
          ) : null}
        </div>
      ) : null}

      {!editing && github && !githubReady && !projectRepo?.github ? (
        <div className="space-y-2">
          <ConnectGitHubGate returnTo={returnTo} teamId={team.id} />
          {canEdit && !remote ? (
            <button
              type="button"
              className="text-xs text-muted-foreground underline"
              onClick={() => {
                setEditing("paste");
                setUrl("");
              }}
            >
              Not on GitHub? Paste a clone URL
            </button>
          ) : null}
        </div>
      ) : null}

      {remote ? (
        <div className="space-y-2">
          <p className="text-sm font-medium">Your computers</p>
          {paired.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No computer paired yet.{" "}
              <Link to="/connect" className="underline">
                Connect a computer
              </Link>{" "}
              to work on this project locally.
            </p>
          ) : (
            <ul className="space-y-2">
              {paired.map((machine) => (
                <MachineRow key={machine.id} machine={machine} team={team} remote={remote} />
              ))}
            </ul>
          )}
        </div>
      ) : null}

      {cloud ? (
        <p className="text-xs text-muted-foreground">
          {defaultCloud
            ? `Deploys to Google Cloud · ${defaultCloud.projectId} (${defaultCloud.region}), your organization's default cloud.`
            : "No cloud connected yet: an organization admin sets it up in Settings → Cloud."}
        </p>
      ) : null}

      {canEdit && projectRepo?.github && !projectRepo.needsAttention ? (
        <RepoVariablesSyncRow teamId={team.id} />
      ) : null}

      {remote ? <ProjectStackCard teamId={team.id} canEdit={canEdit} /> : null}
      {remote ? <ProjectDatabaseCard teamId={team.id} canEdit={canEdit} /> : null}
    </div>
  );
}
